import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useAccountUsers } from "../api/get-account-users"
import { DataTable } from "@/components/DataTable"
import { type ColumnDef } from "@tanstack/react-table"
import * as React from "react"
import UserActions from '@/features/users/components/UserActions'
import { CUSTOMERS_USER_ROLES } from '@/features/Customers/customers.constants'

export default function AccountUsers({ accountId }: { accountId: string }) {
    const usersQuery = useAccountUsers({ accountId: accountId || '' })
    const users = (usersQuery.data as any[]) || []

    const handleResetPassword = (user: any) => {
        // TODO: wire to reset password flow
        console.log('Reset password', user)
    }

    const handleDeleteUser = async (user: any) => {
        // TODO: wire to delete user API
        console.log('Delete user', user)
    }

    const handleView = (user: any) => {
        // TODO: navigate to user view
        console.log('View user', user)
    }

    const handleEdit = (user: any) => {
        // TODO: navigate to user edit
        console.log('Edit user', user)
    }

    const columns = React.useMemo<ColumnDef<any, any>[]>(() => [
        {
            id: 'name',
            header: 'Name',
            accessorFn: (row) => row.userName ?? row.name ?? row.email ?? '—',
            cell: ({ row }) => {
                const original = row.original as any
                const displayName = original.userName ?? original.name ?? original.email ?? '—'
                const email = original.email ?? '—'
                return (
                    <div className='flex flex-col'>
                        <span className='font-medium'>{displayName}</span>
                        <span className='text-xs text-muted-foreground'>{email}</span>
                    </div>
                )
            },
        },
        {
            id: 'role',
            header: 'Role',
            accessorFn: (row) => row.role ?? row.userRole ?? '—',
            cell: ({ getValue }) => {
                const role = getValue()
                const label = typeof role === 'number'
                    ? (CUSTOMERS_USER_ROLES as Record<number, string>)[role] ?? role
                    : role
                return <span>{label ?? '—'}</span>
            },
        },
        {
            id: 'lastLogin',
            header: 'Last Login',
            accessorFn: (row) => row.lastLogin ?? null,
            cell: ({ getValue }) => {
                const v = getValue() as string | null
                const d = v ? new Date(v) : null
                return <span>{d ? d.toLocaleString() : '—'}</span>
            },
        },
        {
            id: 'status',
            header: 'Status',
            accessorFn: (row) => (row.lastLogin ? 'Active' : 'Invited'),
            cell: ({ getValue }) => <span>{getValue() as string}</span>,
        },
        {
            id: 'actions',
            enableHiding: false,
            cell: ({ row }) => {
                const user = row.original as any
                return (
                    <UserActions
                        user={user}
                        onView={handleView}
                        onEdit={handleEdit}
                        onResetPassword={handleResetPassword}
                        onDelete={handleDeleteUser}
                    />
                )
            },
        }
    ], [])
    return (
        <Card>
            <CardHeader>
                <CardTitle>Users</CardTitle>
            </CardHeader>
            <CardContent>
                {usersQuery.isLoading && <div>Loading users...</div>}
                {usersQuery.error && !usersQuery.isLoading && (
                    <div className='text-destructive'>Failed to load users.</div>
                )}
                {!usersQuery.isLoading && !usersQuery.error && (
                    <DataTable
                        columns={columns}
                        data={users}
                        emptyMessage='No users'
                        enablePagination={true}
                        initialPageSize={10}
                        scrollContainerClassName='max-h-[400px] overflow-auto'
                        onRowClick={(row) => {
                            console.log('Row clicked', row.original)
                        }}
                    />
                )}
            </CardContent>
        </Card>
    )
}
